import { defineBlock, cssText, type VNode } from "@broadpaper/core";

/**
 * Structural nodes. They are hidden from the palette; the editor creates them
 * through layout tools (add section, add row with a column layout).
 */

export const sectionBlock = defineBlock<{ padding: number; background?: string; label?: string }>({
  type: "section",
  name: "Section",
  category: "structure",
  icon: "rectangle-horizontal",
  hidden: true,
  defaultProps: { padding: 0 },
  container: { accepts: ["@rows", "@blocks"] },
  split: "children",
  styleGroups: ["spacing", "background", "border", "flow"],
  inspector: [
    {
      title: "Section",
      fields: [
        { kind: "text", prop: "label", label: "Label", placeholder: "e.g. Executive summary", allowTokens: false },
        { kind: "number", prop: "padding", label: "Padding", min: 0, max: 120, unit: "px" }
      ]
    }
  ],
  canvas: { label: (p) => p.label ?? "Section" },
  render: ({ h, css, children, styles, props, mode, node }) => {
    const pad = Number(props.padding) || 0;
    const empty = children.length === 0;
    return h(
      "div",
      { class: ["bp-section__inner", empty && "bp-section__inner--empty"], style: cssText({ ...css, padding: pad ? `${pad}px` : css["padding"] }) },
      h("div", { class: "bp-stack", style: { gap: `${styles.spacing.blockGap}px` }, "data-dropzone": node.nodeId }, empty && mode === "design" ? h("div", { class: "bp-empty-section" }, h("span", { class: "bp-empty-section__title" }, "Empty section"), h("span", { class: "bp-empty-section__hint" }, "Drag blocks here, or pick one from the panel")) : children)
    );
  }
});

export const rowBlock = defineBlock<{ layout: number[]; allowSplit: boolean; verticalAlign: "top" | "middle" | "bottom" | "stretch" }>({
  type: "row",
  name: "Columns",
  description: "A row of columns",
  category: "layout",
  icon: "columns-3",
  keywords: ["row", "columns", "grid", "layout"],
  defaultProps: { layout: [6, 6], allowSplit: false, verticalAlign: "top" },
  container: { accepts: ["@columns"], direction: "row" },
  split: "children",
  styleGroups: ["spacing", "background", "border", "flow"],
  inspector: [
    {
      title: "Columns",
      fields: [
        {
          kind: "segmented",
          prop: "verticalAlign",
          label: "Vertical align",
          options: [
            { value: "top", label: "Top", icon: "align-start-vertical" },
            { value: "middle", label: "Middle", icon: "align-center-vertical" },
            { value: "bottom", label: "Bottom", icon: "align-end-vertical" },
            { value: "stretch", label: "Stretch", icon: "stretch-vertical" }
          ]
        },
        { kind: "toggle", prop: "allowSplit", label: "Allow splitting across pages", description: "When off, a row that does not fit moves to the next page whole." }
      ]
    }
  ],
  canvas: { label: (p) => `Columns ${p.layout.join(" / ")}` },
  render: ({ h, css, children, styles, props }) => {
    const align = props.verticalAlign === "middle" ? "center" : props.verticalAlign === "bottom" ? "end" : props.verticalAlign === "stretch" ? "stretch" : "start";
    return h("div", { class: "bp-row__grid", style: cssText({ ...css, display: "grid", "grid-template-columns": "repeat(12, minmax(0, 1fr))", "column-gap": `${styles.spacing.columnGap}px`, "align-items": align }) }, children);
  }
});

export const columnBlock = defineBlock<{ span: number }>({
  type: "column",
  name: "Column",
  category: "structure",
  icon: "rectangle-vertical",
  hidden: true,
  defaultProps: { span: 6 },
  container: { accepts: ["@blocks", "@rows"] },
  split: "children",
  styleGroups: ["spacing", "background", "border"],
  render: ({ h, css, children, node, styles, mode }) => {
    const empty = children.length === 0;
    return h(
      "div",
      { class: ["bp-column__inner", empty && "bp-column__inner--empty"], style: cssText({ ...css, "min-width": "0", height: "100%" }) },
      h("div", { class: "bp-stack", style: { gap: `${styles.spacing.blockGap}px` }, "data-dropzone": node.nodeId }, empty && mode === "design" ? h("div", { class: "bp-empty-column" }, "Drop here") : children)
    );
  }
});

export const repeaterItemBlock = defineBlock<{ index: number; count: number }>({
  type: "repeater-item",
  name: "Repeater item",
  category: "structure",
  hidden: true,
  defaultProps: { index: 0, count: 0 },
  container: { accepts: ["@blocks", "@rows"] },
  split: "children",
  render: ({ h, children, styles }) => h("div", { class: "bp-stack bp-repeater-item__inner", style: { gap: `${styles.spacing.blockGap}px` } }, children)
});

export function stack(h: typeof import("@broadpaper/core").h, children: VNode[], gap: number, extra: Record<string, unknown> = {}): VNode {
  return h("div", { class: "bp-stack", style: { gap: `${gap}px` }, ...extra }, children);
}
